/**
 * Read adapters for `density-histogram` (plots plan M; docs/deck-design-system/plots/read-adapters.md).
 *
 * Shape only: rename, regroup, copy. No bin, share or statistic is computed.
 *
 * - `deck-slide: continuous-density` WITH `plot` (built from plan M on): the
 *   stamped payload passes through (`readContinuousDensityPlot`).
 * - `deck-slide: continuous-density` WITHOUT `plot` (every deck stored before
 *   M): `continuousDensitySlideAdapter`, LEGACY. `bins[].value` to `share`,
 *   one total series, `summaryStats` to `stats`, `displayLabels` to the label
 *   keys, `displayLabels.xAxisTitle` to `measure.label`. The band is drawn
 *   only when `p25`/`p75` are stored (the producer omitted them below THIN_N),
 *   so `isThinBase` is their absence. A stored slide has no `decimals`, no
 *   `labelForm` and no bin templates, so its numbers and bin names keep the
 *   slide's own rules (`legacyContinuousDensityFormatter`, moved unchanged
 *   from `ContinuousDensitySlide`): stored decks draw exactly as before.
 * - `quick-study: histogram` / `rating`: a result carrying the Python-built
 *   `density_histogram_plot` passes through; a stored result without it keeps
 *   `NumericChart` as its legacy reader (not accepted here).
 * - Study figures (survey tab, D2/D3): Python-built, pass through.
 */

import { ContinuousDensitySlideData } from '../../slides/renderer-internal-types'
import { PlotPayloadOf, PlotReadAdapter, StoredPlotPayload } from '../contract'
import { DensityFormatter, DensityHistogramBinData, DensityHistogramPlotLabels } from './format'

type StoredDensity = StoredPlotPayload<'density-histogram'>
type StoredBin = ContinuousDensitySlideData['bins'][number]
type LegacySlide = ContinuousDensitySlideData & { isIntegerMeasure?: boolean | null; plot?: unknown }

const VALUE_FORMATS = ['mean_out_of_scale', 'percent', 'currency', 'count', 'duration', 'probability', 'index', 'text'] as const
type ValueFormat = (typeof VALUE_FORMATS)[number]

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function finiteOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/** The stamped plot on a slide, when it is this kind's. */
export function readContinuousDensityPlot(slide: unknown): PlotPayloadOf<'density-histogram'> | null {
  if (!isRecord(slide) || !isRecord(slide.plot)) {
    return null
  }
  const plot = slide.plot
  if (plot.kind !== 'density-histogram' || !isRecord(plot.data) || !Array.isArray(plot.data.series)) {
    return null
  }
  return plot as unknown as PlotPayloadOf<'density-histogram'>
}

/**
 * LEGACY (stored slides without `isOverflow`): the tail bin is an overflow
 * bin when its span is well over the regular step. Today's slide rule, moved.
 */
function legacyOverflowIndex(bins: Array<StoredBin>): number | null {
  if (bins.length < 2) {
    return null
  }
  const step = bins[0].max - bins[0].min
  const last = bins[bins.length - 1]
  const lastSpan = last.max - last.min
  const hasOverflow = typeof last.isOverflow === 'boolean' ? last.isOverflow : step > 0 && lastSpan > step * 1.5
  return hasOverflow ? bins.length - 1 : null
}

/** LEGACY: a stored slide without `isIntegerMeasure` reads count and index as whole. */
function legacyIsIntegerMeasure(slide: LegacySlide): boolean {
  if (typeof slide.isIntegerMeasure === 'boolean') {
    return slide.isIntegerMeasure
  }
  const format = slide.valueFormat || 'count'
  return format === 'count' || format === 'index'
}

export const continuousDensitySlideAdapter: PlotReadAdapter<'density-histogram', LegacySlide> = {
  kind: 'density-histogram',
  source: { surface: 'deck-slide', shape: 'continuous-density' },
  isLegacy: true,
  accepts(stored: unknown): stored is LegacySlide {
    return isRecord(stored) && stored.type === 'continuous-density' && Array.isArray(stored.bins)
  },
  read(stored: LegacySlide): StoredDensity {
    const bins = stored.bins || []
    const overflow = legacyOverflowIndex(bins)
    const stats = stored.summaryStats || {}
    const displayLabels = stored.displayLabels || {}
    const labels: DensityHistogramPlotLabels = {}
    if (displayLabels.mean) labels.mean = displayLabels.mean
    if (displayLabels.median) labels.median = displayLabels.median
    if (displayLabels.yAxisTitle) labels.continuous_density_y_axis_title = displayLabels.yAxisTitle
    if (displayLabels.bandLegend) labels.continuous_density_iqr_band_legend = displayLabels.bandLegend
    const valueFormat: ValueFormat = (VALUE_FORMATS as ReadonlyArray<string>).includes(stored.valueFormat || '')
      ? (stored.valueFormat as ValueFormat)
      : 'count'
    const p25 = finiteOrNull(stats.p25)
    const p75 = finiteOrNull(stats.p75)

    return {
      kind: 'density-histogram',
      data: {
        measure: {
          label: displayLabels.xAxisTitle ?? null,
          valueFormat,
          currencyCode: stored.currencyCode ?? null,
          durationUnit: stored.durationUnit ?? null,
          isIntegerMeasure: legacyIsIntegerMeasure(stored),
          // Not stored: the legacy formatter prints by the slide's own rule.
          decimals: 0,
        },
        series: [
          {
            id: 'total',
            label: null,
            colorSlot: 1,
            // Not stored on the slide, and nothing on the slide prints it.
            base: 0,
            bins: bins.map((bin, index) => ({
              id: bin.id,
              min: bin.min,
              max: bin.max,
              share: typeof bin.value === 'number' ? bin.value : 0,
              count: typeof bin.count === 'number' ? bin.count : null,
              labelForm: index === overflow ? 'openTop' : 'halfOpen',
            })),
            stats: {
              mean: finiteOrNull(stats.mean),
              median: finiteOrNull(stats.median),
              min: finiteOrNull(stats.min),
              max: finiteOrNull(stats.max),
              p25,
              p75,
            },
            isThinBase: p25 == null || p75 == null,
          },
        ],
        totalSeriesCount: 1,
        labels,
      },
    }
  },
}

// -- Legacy formatting (today's ContinuousDensitySlide rules, moved unchanged) --

function magnitudeDigits(value: number) {
  const abs = Math.abs(value)
  if (abs >= 100) return 0
  if (abs >= 10) return 1
  return 2
}

// legacy reader only: the stored-slide formatters below.
function fmt(value?: number | null, digits?: number) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'N/A'
  if (Number.isInteger(value)) return value.toFixed(0)
  return value.toFixed(digits ?? magnitudeDigits(value))
}

function fmtCurrency(value: number, slide: LegacySlide, compact: boolean) {
  const digits = compact || Number.isInteger(value) ? 0 : magnitudeDigits(value)
  const code = typeof slide.currencyCode === 'string' ? slide.currencyCode.trim() : ''
  if (!code) {
    return value.toFixed(digits)
  }
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: digits,
      minimumFractionDigits: digits,
    }).format(value)
  } catch {
    return `${code} ${value.toFixed(digits)}`
  }
}

function durationLabel(value: string | null | undefined, compact: boolean) {
  if (!value) return compact ? 'u' : 'units'
  if (!compact) return value
  return ({
    minutes: 'min',
    hours: 'hr',
    days: 'd',
    weeks: 'wk',
    months: 'mo',
  } as Record<string, string>)[value] || value
}

/** LEGACY: today's magnitude rule (>=100 no decimals, >=10 one, else two), en-US. */
export function legacyDensityMetric(value: number | null | undefined, slide: LegacySlide, compact = false) {
  // An empty lane (no answers, so no plot) prints the stamped word; the English reads a stored deck.
  if (typeof value !== 'number' || !Number.isFinite(value)) return slide.displayLabels?.notAvailable ?? 'N/A'
  const format = slide.valueFormat || 'count'
  if (format === 'currency') return fmtCurrency(value, slide, compact)
  if (format === 'percent') return `${fmt(value, compact ? 0 : undefined)}%`
  if (format === 'duration') return `${fmt(value, compact ? 0 : undefined)} ${durationLabel(slide.durationUnit || slide.unit, compact)}`
  if (format === 'probability') return compact ? fmt(value) : `${fmt(value)} prob.`
  if (format === 'index') return compact ? fmt(value, 0) : `${fmt(value)} index`
  return fmt(value)
}

/** LEGACY: the x-axis title a stored slide without `displayLabels.xAxisTitle` printed. */
export function legacyDensityUnitLabel(slide: LegacySlide) {
  const format = slide.valueFormat || 'count'
  if (format === 'currency') return slide.currencyCode || 'Price'
  if (format === 'duration') return durationLabel(slide.durationUnit || slide.unit, false)
  if (format === 'percent') return 'Percent'
  if (format === 'probability') return 'Probability'
  if (format === 'index') return 'Index'
  return slide.unit || 'Count'
}

/** LEGACY: a stored slide's numbers and bin names, exactly as it printed them. */
export function legacyContinuousDensityFormatter(slide: LegacySlide): DensityFormatter {
  const isIntegerMeasure = legacyIsIntegerMeasure(slide)

  return {
    value(value) {
      return legacyDensityMetric(value, slide)
    },
    median(value) {
      return legacyDensityMetric(value, slide)
    },
    annotation(value) {
      return legacyDensityMetric(value, slide, true)
    },
    binLabel(bin: DensityHistogramBinData) {
      if (bin.labelForm === 'openTop') {
        return `${legacyDensityMetric(bin.min, slide, true)}+`
      }
      if (isIntegerMeasure) {
        return `${legacyDensityMetric(bin.min, slide, true)}-${legacyDensityMetric(bin.max - 1, slide, true)}`
      }
      return `${legacyDensityMetric(bin.min, slide, true)} to <${legacyDensityMetric(bin.max, slide, true)}`
    },
    range(low, high) {
      return `${legacyDensityMetric(low, slide, true)} - ${legacyDensityMetric(high, slide, true)}`
    },
  }
}
